import { useHomeUsePluginContainer } from "@custom-hooks/HomeUsePluginRoot";
import { Api } from "@uikit/service/api";
import { getIt } from "@uikit/getIt";
import { BrainClient } from "imagica-corekit/dist/base/api/BrainClient";
import { clone, find, get, map, set } from "lodash";
import { useStore } from "react-redux";
import { Node, useReactFlow } from "reactflow";
import { BlueprintFunctionType } from "imagica-corekit/dist/base/storyV2/function/BlueprintFunction";

const getNodeInfo = (node: Node<any> | undefined) => {
  const textareaValue = get(node, "data.textAreaValue");
  return {
    node_id: node?.id || "",
    node_label: get(node, "data.inputTitle") || get(node, "data.generatedLabel", ""),
    node_placeholder: get(node, "data.placeholder", ""),
    node_value: typeof textareaValue === "string" ? textareaValue : undefined,
  };
};

export function useUpdateNodeEdgeInfo() {
  const brainClient = getIt(BrainClient);
  const store = useStore();
  const api = new Api(false);
  const { setNodes, getNodes } = useReactFlow();
  const {
    nodesState: { nodesRef },
  } = useHomeUsePluginContainer();

  const updateEdgeText = async (id: string) => {
    if (get(store.getState(), "editor.me.feature_tags", [] as string[]).includes("disable_update_edge_text_by_api")) {
      return undefined;
    }

    const targetNode = find(nodesRef.current, node => node.id === id);
    const input_nodes = map(get(targetNode, "data.flows"), ({ sourceNodeId }) => {
      const node = find(nodesRef.current, node => node.id === sourceNodeId);
      return getNodeInfo(node);
    });
    const { data } = await brainClient.openAi.updateEdge({
      input_nodes,
      edge_description: get(targetNode, "data.lineParam.enterText"),
    });

    const edgeText = data.edge_description.replace(/"/g, "");

    setNodes(preNodes => {
      return map(preNodes, node => {
        if (node.id === id) {
          return set({ ...node }, "data.lineParam.enterText", edgeText);
        }
        const info = find(data.input_nodes, data => data.node_id === node.id);
        if (info) {
          return set({ ...node }, "data", {
            ...node.data,
            generatedLabel: info.node_label,
            placeholder: info.node_placeholder,
          });
        }
        return node;
      });
    });

    return edgeText;
  };

  const updateBlueprintEdge = async (target: string, edge_description: string) => {
    const nodes = getNodes();
    const targetNode = nodes.find(x => x.id === target);
    const flows = targetNode?.data.flows.map((x: any) => x.sourceNodeId);
    const inputs: any[] = nodes
      .filter(x => flows.includes(x.id))
      .map(x => {
        return {
          name: x.data?.inputTitle || x.data?.generatedLabel || "input",
          type: x.data.displayType,
          description: x.data.placeholder,
        };
      });
    const res = await api.postWithError(`/be/langchain/blueprint_builder`, { edge_description, inputs });
    setNodes((prevList: any[]) =>
      prevList.map(x => {
        if (x.id === target) {
          return {
            ...x,
            data: {
              ...x.data,
              lineParam: {
                ...x.data.lineParam,
                blueJson: JSON.stringify({
                  ...res.data?.blueprint,
                  functionType: BlueprintFunctionType.EXECUTE_BLUEPRINT,
                }),
              },
            },
          };
        }
        return x;
      })
    );
  };

  const updateNodeText = async (id: string, text: string) => {
    const targetNode = find(nodesRef.current, node => node.id === id);
    const edgeText = get(targetNode, "data.lineParam.enterText");

    if (text === edgeText) {
      return;
    }

    if (get(store.getState(), "editor.me.feature_tags", [] as string[]).includes("disable_update_node_text_by_api")) {
      return undefined;
    }

    const flows = clone(get(targetNode, "data.flows") || []);
    const input_nodes = map(flows, ({ sourceNodeId }) => {
      const node = find(nodesRef.current, node => node.id === sourceNodeId);
      return getNodeInfo(node);
    });

    const { data } = await brainClient.openAi.updateNode({
      input_nodes,
      edge_description: text,
      number_of_inputs: input_nodes.length,
    });

    setNodes(preNodes => {
      return map(preNodes, node => {
        const info = find(
          data.input_nodes,
          data => data.node_id === node.id && input_nodes.some(n => n.node_id === data.node_id)
        );
        if (info) {
          return set({ ...node }, "data", {
            ...node.data,
            generatedLabel: info.node_label,
            placeholder: info.node_placeholder,
          });
        }
        return node;
      });
    });
  };

  return {
    updateBlueprintEdge,
    updateEdgeText,
    updateNodeText,
  };
}
